import React, { useState, useEffect } from 'react';
import api from '../../api/api';
import AdminLayout from '../../components/AdminLayuot';
import { 
    Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Cell, ComposedChart, Line, Legend, ReferenceLine, BarChart
} from 'recharts';

const THEME = {
    primary: '#1F2937',
    secondary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    bg: '#F3F4F6',
    white: '#FFFFFF',
    border: '#E5E7EB'
};

function AdminDesviaciones() {
    const [allData, setAllData] = useState([]); 
    const [filteredData, setFilteredData] = useState([]);
    const [selectedClient, setSelectedClient] = useState('all'); 
    const [clientList, setClientList] = useState([]);

    const [stats, setStats] = useState({ 
        stdDev: 0, mean: 0, mape: 0,      
        meanDays: 0, stdDevDays: 0        
    }); 
    
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        applyFilter();
    }, [selectedClient, allData]);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/predicciones/analisis-desviacion', {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            const rawData = response.data;
            const validData = rawData.filter(item => item.pedido);

            const preProcessed = validData.map((item) => {
                const dPred = new Date(item.fecha);
                dPred.setHours(0,0,0,0);

                const fechaRealSource = item.pedido.fechaEntrega || item.pedido.createdAt;
                const dReal = new Date(fechaRealSource);
                dReal.setHours(0,0,0,0);
                
                const diffTime = dReal - dPred;
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                
                const kgPred = parseFloat(item.cantidad) || 0;

                // ✅ ACTUALIZACIÓN: Sumar Grano + Molido del nuevo esquema de pedidos
                const kgReal = (Number(item.pedido.cantidadGrano) || 0) + (Number(item.pedido.cantidadMolido) || 0);
                
                const diffKg = kgReal - kgPred;
                const errorKgPct = kgPred !== 0 ? ((diffKg / kgPred) * 100).toFixed(1) : 0;
                
                const dateObj = new Date(item.fecha);

                return {
                    ...item,
                    cliente: item.usuario?.username || 'Anon',
                    fechaObj: dateObj,
                    shortDate: dateObj.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
                    fechaDisplay: dateObj.toLocaleDateString(),
                    fechaRealDisplay: dReal.toLocaleDateString(),
                    kgPred, kgReal, diffDays, diffKg,
                    errorKgPct: parseFloat(errorKgPct)
                };
            });

            const uniqueClients = [...new Set(preProcessed.map(item => item.cliente))];
            setClientList(uniqueClients.sort());
            setAllData(preProcessed);

        } catch (error) {
            console.error("Error cargando análisis", error);
        } finally {
            setLoading(false);
        }
    };

    const applyFilter = () => {
        if (allData.length === 0) return;

        let temp = selectedClient === 'all' 
            ? allData 
            : allData.filter(d => d.cliente === selectedClient);

        const dataWithLabels = temp.map((item, index) => ({
            ...item,
            chartLabel: `${item.cliente.substring(0, 6)} (${item.shortDate}) #${index + 1}`
        }));

        if (dataWithLabels.length > 0) {
            const diffsKg = dataWithLabels.map(d => d.diffKg);
            const meanKg = diffsKg.reduce((a, b) => a + b, 0) / diffsKg.length;
            const varKg = diffsKg.reduce((a, b) => a + Math.pow(b - meanKg, 2), 0) / diffsKg.length;
            const stdDevKg = Math.sqrt(varKg);
            const absPctErrors = dataWithLabels.map(d => Math.abs(d.errorKgPct));
            const mape = absPctErrors.reduce((a, b) => a + b, 0) / absPctErrors.length;

            const diffsDays = dataWithLabels.map(d => d.diffDays);
            const meanDays = diffsDays.reduce((a, b) => a + b, 0) / diffsDays.length;
            const varDays = diffsDays.reduce((a, b) => a + Math.pow(b - meanDays, 2), 0) / diffsDays.length;
            const stdDevDays = Math.sqrt(varDays);

            setStats({ 
                mean: parseFloat(meanKg.toFixed(2)), 
                stdDev: parseFloat(stdDevKg.toFixed(2)),
                mape: parseFloat(mape.toFixed(1)),
                meanDays: parseFloat(meanDays.toFixed(1)),
                stdDevDays: parseFloat(stdDevDays.toFixed(1))
            });
        } else {
            setStats({ stdDev: 0, mean: 0, mape: 0, meanDays: 0, stdDevDays: 0 });
        }
        setFilteredData(dataWithLabels);
    };

    const getDayStatusBadge = (diffDays) => {
        const absDays = Math.abs(diffDays);
        let config = { text: 'Fuera de rango', bg: '#fee2e2', color: '#991b1b' };
        if (absDays <= 1) config = { text: 'Preciso', bg: '#dcfce7', color: '#166534' };
        else if (absDays <= 3) config = { text: 'Aceptable', bg: '#fef9c3', color: '#854d0e' };
        return <span style={{ ...badgeStyle, background: config.bg, color: config.color }}>{config.text}</span>;
    };

    if (loading) return <AdminLayout title="Análisis de Precisión"><div style={{textAlign:'center', marginTop:50}}>Calculando desviaciones...</div></AdminLayout>;

    return (
        <AdminLayout title="🎯 Análisis de Precisión y Error">
            
            {/* FILTROS */}
            <div style={{ background: 'white', padding: 15, borderRadius: 12, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 25, boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <label style={{fontWeight: 'bold', color: THEME.primary, fontSize: 14}}>Seleccionar Cliente:</label>
                    <select 
                        value={selectedClient} 
                        onChange={(e) => setSelectedClient(e.target.value)}
                        style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${THEME.border}`, minWidth: 200 }}
                    >
                        <option value="all">-- Todos los Clientes --</option>
                        {clientList.map(client => (
                            <option key={client} value={client}>{client}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* SECCIÓN KILOS */}
            <h2 style={sectionTitleStyle}>⚖️ Precisión de Cantidad (Kg)</h2>
            <div style={{ display: 'flex', gap: 20, marginBottom: 25, flexWrap: 'wrap' }}>
                <KPICard title="Error Promedio" value={`${stats.mean > 0 ? '+' : ''}${stats.mean} Kg`} color={stats.mean > 0 ? THEME.success : THEME.danger} />
                <KPICard title="Desviación Estándar" value={`±${stats.stdDev} Kg`} color={THEME.primary} />
                <div style={kpiCardStyle}>
                    <div style={{display:'flex', alignItems:'center', justifyContent:'center', gap:10}}>
                        <div style={{height: 60, width: 60}}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={[{ value: Math.min(stats.mape, 100) }, { value: Math.max(0, 100 - stats.mape) }]} innerRadius={18} outerRadius={25} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
                                        <Cell fill={stats.mape < 15 ? THEME.success : THEME.danger} />
                                        <Cell fill="#e5e7eb" />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                        <div style={{textAlign:'left'}}>
                            <h4 style={{margin:0, color:'#64748b', fontSize: 12}}>MAPE Global</h4>
                            <span style={{fontSize: 22, fontWeight:'bold', color: stats.mape < 15 ? THEME.success : THEME.danger}}>{stats.mape}%</span>
                        </div>
                    </div>
                </div>
            </div>

            <div style={chartContainerStyle}>
                <h3 style={{textAlign:'center', fontSize: 16, marginBottom: 20}}>Diferencia de Kilos (Real vs Predicho)</h3>
                <div style={{ height: 350 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={filteredData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="chartLabel" fontSize={10} tickFormatter={(val) => val.split(' (')[0]} />
                            <YAxis yAxisId="left" fontSize={12} />
                            <YAxis yAxisId="right" orientation="right" fontSize={12} />
                            <Tooltip />
                            <Legend />
                            <ReferenceLine yAxisId="left" y={0} stroke="#000" />
                            <Bar yAxisId="left" dataKey="diffKg" name="Diferencia Kg" barSize={35}>
                                {filteredData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.diffKg >= 0 ? THEME.success : THEME.danger} />
                                ))}
                            </Bar>
                            <Line yAxisId="right" type="monotone" dataKey="errorKgPct" name="Error %" stroke={THEME.warning} strokeWidth={2} />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* SECCIÓN TIEMPO */}
            <h2 style={{...sectionTitleStyle, marginTop: 50}}>🗓️ Precisión de Tiempo (Días)</h2>
            <div style={{ display: 'flex', gap: 20, marginBottom: 25, flexWrap: 'wrap' }}>
                <KPICard title="Error Promedio" value={`${stats.meanDays > 0 ? '+' : ''}${stats.meanDays} días`} color={stats.meanDays === 0 ? THEME.success : THEME.danger} />
                <KPICard title="Volatilidad" value={`±${stats.stdDevDays} días`} color={THEME.primary} />
                <KPICard title="Pedidos Exactos" value={filteredData.filter(d => d.diffDays === 0).length} color={THEME.success} note={`De ${filteredData.length} totales`} />
            </div>

            <div style={chartContainerStyle}>
                <h3 style={{textAlign:'center', fontSize: 16, marginBottom: 20}}>Desviación de Días (Adelanto/Atraso)</h3>
                <div style={{ height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={filteredData}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="chartLabel" fontSize={10} tickFormatter={(val) => val.split(' (')[0]} />
                            <YAxis fontSize={12} />
                            <Tooltip />
                            <ReferenceLine y={0} stroke="#000" />
                            <Bar dataKey="diffDays" name="Diferencia Días" barSize={40}>
                                {filteredData.map((entry, index) => (
                                    <Cell key={`cell-day-${index}`} fill={entry.diffDays === 0 ? THEME.success : entry.diffDays > 0 ? THEME.danger : THEME.secondary} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* TABLA DETALLE */}
            <h3 style={{marginTop: 40, color: THEME.primary}}>Desglose de Desviaciones</h3>
            <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ background: THEME.primary, color: 'white', textAlign: 'left' }}>
                            <th style={thStyle}>Cliente</th>
                            <th style={thStyle}>Comparativa</th>
                            <th style={thStyle}>Diferencia Días</th>
                            <th style={thStyle}>Diferencia Kg</th>
                            <th style={thStyle}>Error %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredData.map((item) => (
                            <tr key={item.id} style={{ borderBottom: `1px solid ${THEME.border}` }}>
                                <td style={tdStyle}>
                                    <div style={{fontWeight:'bold'}}>{item.cliente}</div>
                                    <div style={{fontSize:11, color:'#6B7280'}}>{item.usuario?.perfil?.restaurant}</div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={{fontSize:11}}>📅 P: {item.fechaDisplay} | R: {item.fechaRealDisplay}</div>
                                    <div style={{fontSize:11}}>⚖️ P: {item.kgPred}kg | R: {item.kgReal}kg</div>
                                </td>
                                <td style={tdStyle}>
                                    <div style={{fontWeight: 'bold', color: item.diffDays === 0 ? THEME.success : item.diffDays > 0 ? THEME.danger : THEME.secondary}}>
                                        {item.diffDays > 0 ? `+${item.diffDays}` : item.diffDays} días
                                    </div>
                                    {getDayStatusBadge(item.diffDays)}
                                </td>
                                <td style={tdStyle}>
                                    <div style={{fontWeight: 'bold', color: item.diffKg >= 0 ? THEME.success : THEME.danger}}>
                                        {item.diffKg > 0 ? '+' : ''}{item.diffKg.toFixed(1)} Kg
                                    </div>
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontWeight:'bold', color: Math.abs(item.errorKgPct) > 15 ? THEME.danger : THEME.success }}>
                                        {item.errorKgPct}%
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </AdminLayout>
    );
}

// --- COMPONENTES AUXILIARES ---
const KPICard = ({ title, value, color, note }) => (
    <div style={kpiCardStyle}>
        <h4 style={{margin:0, color:'#64748b', fontSize: 13}}>{title}</h4>
        <div style={{fontSize: 28, fontWeight:'bold', color}}>{value}</div>
        {note && <div style={{fontSize: 11, color: '#94a3b8', marginTop: 4}}>{note}</div>}
    </div>
);

// --- ESTILOS ---
const sectionTitleStyle = { color: THEME.primary, marginBottom: 20, borderBottom: `2px solid ${THEME.border}`, paddingBottom: 10, fontSize: 20 };
const kpiCardStyle = { flex: 1, minWidth: 220, background: 'white', padding: 20, borderRadius: 12, textAlign: 'center', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${THEME.border}` };
const chartContainerStyle = { background: 'white', borderRadius: 12, padding: 25, boxShadow: '0 4px 6px rgba(0,0,0,0.05)', border: `1px solid ${THEME.border}` };
const thStyle = { padding: '15px' };
const tdStyle = { padding: '15px' };
const badgeStyle = { padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold', fontSize: '10px', display: 'inline-block', marginTop: 4 };

export default AdminDesviaciones;